import { useState, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { MONO, eurK, eurS } from './format'
import type { Agg } from './aggregate'
import { groupSeries, levelTotal, levelValues, seriesFill, type Series } from './levels'
import type { BreakdownDim, MonthCash } from '../types'

export type BreakdownView = 'By month' | 'vs Prior'

const PRIOR = '#E2DCD2' // beige — prior period
const UP = '#2C7D52' // green — grew
const DOWN = '#C8453E' // red — declined
const H = 200
const PILL_H = 132 // approximate pill height, used to clamp it inside the chart
const PILL_W = 252

interface BreakdownChartProps {
  view: BreakdownView
  months: MonthCash[]
  cur: Agg
  prev: Agg
  /** The level shown: Business unit | Department | Category. */
  dim: BreakdownDim
  /** The series to draw at that level, in display order (see ./levels). */
  series: Series[]
  currSpan: string
  priorSpan: string
  /** Opens the drill for a series key, for one month (By month) or the window (vs Prior). */
  onDrill?: (key: string, month?: string) => void
}

/** Floating comparison pill (vs Prior view), positioned by the caller via `style`. */
function ComparePill({
  category,
  prev,
  curr,
  span,
  priorSpan,
  currSpan,
  style,
}: {
  category: string
  prev: number
  curr: number
  span: number
  priorSpan: string
  currSpan: string
  style: CSSProperties
}) {
  const up = span >= 0
  const pct = prev ? Math.round((span / Math.abs(prev)) * 100) : 0
  return (
    <div
      className="pointer-events-none z-30 hidden rounded-xl border border-stone-700 bg-stone-900 text-left text-[11px] shadow-xl sm:block"
      style={{ ...style, width: PILL_W, padding: 16 }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#d6d3d1', paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        {category}
      </p>
      <div className="text-[11px]" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="flex items-center justify-between gap-3">
          <span className="whitespace-nowrap" style={{ color: '#a8a29e' }}>{priorSpan}</span>
          <span className={cn('shrink-0', MONO)} style={{ color: '#d6d3d1' }}>{eurS(prev)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="whitespace-nowrap" style={{ color: '#a8a29e' }}>{currSpan}</span>
          <span className={cn('shrink-0', MONO)} style={{ color: '#ffffff' }}>{eurS(curr)}</span>
        </div>
        <div className="flex items-center justify-between gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
          <span style={{ color: '#a8a29e' }}>Change</span>
          <span className={cn('shrink-0 font-semibold', MONO)} style={{ color: up ? '#34d399' : '#f87171' }}>
            {eurS(span)} ({pct > 0 ? '+' : ''}{pct}%)
          </span>
        </div>
      </div>
    </div>
  )
}

/** Breakdown chart at one level of the classification: per-month composition
 *  (detail strip below) and per-series current-vs-prior comparison (floating
 *  pill). Negative amounts (an unresolved credit) are listed but never stacked. */
export function BreakdownChart({ view, months, cur, prev, dim, series, currSpan, priorSpan, onDrill }: BreakdownChartProps) {
  const [hov, setHov] = useState<string | null>(null)
  const [hovSeg, setHovSeg] = useState<string | null>(null)

  if (view === 'By month') {
    const byMonth = months.map((m) => levelValues(m, dim))
    const totals = byMonth.map(levelTotal)
    const max = Math.max(1, ...totals)

    const selIdx = hov ? months.findIndex((m) => m.key === hov) : -1
    const si = selIdx >= 0 ? selIdx : months.length - 1
    const sel = months[si]
    const selD = byMonth[si] ?? {}
    const selTotal = totals[si] ?? 0
    const groups = groupSeries(series)

    return (
      <div>
        <div style={{ overflowX: 'auto' }}>
          <div className="flex items-end" style={{ width: 'max-content', marginLeft: 'auto', marginRight: 'auto', gap: 44 }}>
            {months.map((m, mi) => {
            const d = byMonth[mi]
            const total = totals[mi]
            const barPx = Math.max(0, Math.round((total / max) * H))
            // Stack the positive amounts only; the bar's height is the level's net total.
            const posSum = series.reduce((a, s) => a + Math.max(0, d[s.key] || 0), 0) || 1
            const isHov = hov === m.key
            return (
              <div
                key={m.key}
                className="flex shrink-0 flex-col items-center gap-1.5"
                style={{ width: 64 }}
                onMouseEnter={() => setHov(m.key)}
                onMouseLeave={() => {
                  setHov(null)
                  setHovSeg(null)
                }}
              >
                <span className={cn('text-[10px]', isHov ? 'font-medium text-stone-700 dark:text-stone-200' : 'text-stone-500 dark:text-stone-400', MONO)}>
                  {eurK(total)}
                </span>
                <div
                  className={cn(
                    'flex w-full flex-col-reverse overflow-hidden rounded ring-1 transition-shadow',
                    isHov ? 'ring-stone-900/20 dark:ring-white/25' : 'ring-stone-900/5 dark:ring-white/10',
                  )}
                  style={{ height: barPx }}
                >
                  {series.map((s) => {
                    const v = d[s.key] || 0
                    if (v <= 0) return null
                    const segPx = Math.max(1, Math.round((v / posSum) * barPx))
                    const faded = isHov && hovSeg !== null && hovSeg !== s.key
                    return (
                      <button
                        key={s.key}
                        type="button"
                        aria-label={`${s.label}, ${m.label}: ${eurS(v)}`}
                        onClick={() => onDrill?.(s.key, m.label)}
                        onMouseEnter={() => setHovSeg(s.key)}
                        className={cn('w-full cursor-pointer transition-opacity', faded ? 'opacity-40' : 'opacity-100')}
                        style={{ height: segPx, ...seriesFill(s) }}
                      />
                    )
                  })}
                </div>
                <span className={cn('text-[10px]', isHov ? 'font-medium text-stone-600 dark:text-stone-300' : 'text-stone-400')}>{m.label}</span>
              </div>
            )
          })}
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50/60 px-4 py-3 dark:border-stone-800 dark:bg-stone-800/30">
          <div className="mb-2.5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400">{sel?.label}</span>
            <span className={cn('text-sm font-semibold', MONO)}>{eurS(selTotal)}</span>
            <span className="text-[10px] text-stone-400">
              · {hov ? 'hover a segment to highlight · click to open transactions' : 'latest month · hover a bar to inspect'}
            </span>
          </div>
          <div className="space-y-2">
            {groups.map((g) => (
              <div key={g.group ?? 'all'} className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                {g.group && (
                  <span className="w-full text-[10px] font-semibold uppercase tracking-wider text-stone-400 sm:w-32 sm:shrink-0">{g.group}</span>
                )}
                {g.items.map((s) => {
                  const on = hovSeg === s.key
                  return (
                    <span
                      key={s.key}
                      className={cn('inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors', on ? 'bg-champagne-100 dark:bg-champagne-500/20' : '')}
                    >
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={seriesFill(s)} />
                      <span className={cn(s.unresolved && 'italic', on ? 'font-semibold text-stone-900 dark:text-white' : 'text-stone-600 dark:text-stone-300')}>{s.name}</span>
                      <span className={cn(MONO, on ? 'font-semibold text-stone-900 dark:text-white' : 'text-stone-500 dark:text-stone-400')}>{eurS(selD[s.key] || 0)}</span>
                    </span>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // vs Prior — per-series comparison with a clamped floating pill
  const curV = levelValues(cur, dim)
  const prevV = levelValues(prev, dim)
  const max = Math.max(1, ...series.map((s) => Math.max(curV[s.key] || 0, prevV[s.key] || 0)))
  const barHeight = (v: number) => (v > 0 ? Math.max(2, Math.round((v / max) * H)) : v < 0 ? 2 : 0)
  const n = series.length

  return (
    <div>
      {/* Phones: a long level (Category) scrolls sideways instead of crushing the bars. */}
      <div className="overflow-x-auto sm:overflow-visible">
        <div className="min-w-[calc(var(--cols)*44px)] px-2 sm:min-w-0 sm:px-0" style={{ '--cols': n } as CSSProperties}>
          {/* Bars fill the full width and sit on a baseline axis. Equal-width bars
              look uneven when thin and clustered (a short bar reads as wider than a
              tall one), so they're wide, evenly distributed, and share a baseline. */}
          <div className="flex items-end gap-2 border-b border-stone-200 sm:gap-3 dark:border-stone-700" style={{ height: H }}>
            {series.map((s, i) => {
              const cv = curV[s.key] || 0
              const pv = prevV[s.key] || 0
              const delta = cv - pv
              const up = delta >= 0
              const cpx = barHeight(cv)
              const ppx = barHeight(pv)
              const bt = Math.max(cpx, ppx)
              // Anchor the pill just above the bar top, clamped so it never leaves the chart.
              const bottom = Math.max(8, Math.min(bt + 12, H - PILL_H))
              // Edge columns pin the pill to the chart's edge instead of centring it.
              const edge = n > 4 && i < 2 ? { left: 0 } : n > 4 && i > n - 3 ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' }
              return (
                <div
                  key={s.key}
                  className="relative flex h-full min-w-0 flex-1 items-end justify-center gap-1.5 sm:gap-2"
                  onMouseEnter={() => setHov(s.key)}
                  onMouseLeave={() => setHov(null)}
                >
                  {hov === s.key && (
                    <ComparePill
                      category={s.label}
                      prev={pv}
                      curr={cv}
                      span={delta}
                      priorSpan={priorSpan}
                      currSpan={currSpan}
                      style={{ position: 'absolute', bottom, ...edge }}
                    />
                  )}
                  <div className="flex-1 rounded-t" style={{ height: ppx, maxWidth: 36, minWidth: 0, backgroundColor: PRIOR }} />
                  <button
                    type="button"
                    aria-label={`${s.label}: ${eurS(cv)} in ${currSpan} vs ${eurS(pv)} in ${priorSpan}`}
                    onClick={() => onDrill?.(s.key)}
                    className="flex-1 cursor-pointer rounded-t transition-opacity hover:opacity-90"
                    style={{ height: cpx, maxWidth: 36, minWidth: 0, backgroundColor: up ? UP : DOWN }}
                  />
                </div>
              )
            })}
          </div>
          <div className="mt-2 flex gap-2 sm:gap-3">
            {series.map((s) => (
              // Flex-centred so a word wider than its column (e.g. "Interdisciplinary")
              // overflows evenly on both sides instead of running into the next label.
              <span
                key={s.key}
                className={cn(
                  'flex min-w-0 flex-1 flex-col items-center px-0.5 text-center text-[9px] leading-tight sm:text-[10px]',
                  s.unresolved && 'italic',
                  hov === s.key ? 'font-semibold text-stone-700 dark:text-stone-200' : 'text-stone-500 dark:text-stone-400',
                )}
              >
                <span>{s.name}</span>
                {s.group && <span className="mt-0.5 font-normal not-italic text-stone-400 dark:text-stone-500">{s.group}</span>}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-stone-500 dark:text-stone-400">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: PRIOR }} />Prior ({priorSpan})</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: UP }} />Current — grew</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DOWN }} />Current — declined</span>
        <span className="ml-auto text-stone-400">hover a bar for detail · click to open transactions</span>
      </div>
    </div>
  )
}
